import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'react-jss';
import { makePrioStyles } from '../../../../theme/utils';
import { PrioTheme } from '../../../../theme/types';
import { Modal } from '@prio365/prio365-react-library';
import Flex from '../../../../components/Flex';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import { ProjectId } from '../../../../models/Types';
import { CalendarEvent } from '../../../../models/Calendar';
import {
  apiCreateCalendarEvent,
  IcsImportPreviewResponse,
} from '../../../calendar/api';
import { notification, Tag, Descriptions, Checkbox } from 'antd';

const useStyles = makePrioStyles((theme: PrioTheme) => ({
  root: {},
  eventPreview: {
    marginBottom: theme.old.spacing.unit(2),
  },
  calendarSection: {
    marginTop: theme.old.spacing.unit(2),
    padding: theme.old.spacing.unit(1.5),
    border: `1px solid ${theme.old.borders.colors.content}`,
    borderRadius: theme.old.borders.radius,
  },
  calendarRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${theme.old.spacing.unit(0.5)}px 0`,
  },
  calendarLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.old.spacing.unit(1),
    fontWeight: 500,
  },
  existsTag: {
    marginLeft: theme.old.spacing.unit(1),
  },
}));

interface IcsImportModalProps {
  visible: boolean;
  onClose: () => void;
  projectId: ProjectId;
  previewData: IcsImportPreviewResponse | null;
  projectPreviewData: IcsImportPreviewResponse | null;
  isMe: boolean;
}

export const IcsImportModal: React.FC<IcsImportModalProps> = (props) => {
  //#region ------------------------------ Defaults
  const { visible, onClose, projectId, previewData, projectPreviewData, isMe } =
    props;
  const classes = useStyles();
  const theme = useTheme<PrioTheme>();
  const { t } = useTranslation();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [loading, setLoading] = useState(false);
  const [importPersonal, setImportPersonal] = useState(
    !previewData?.alreadyExists
  );
  const [importProject, setImportProject] = useState(false);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const event = useMemo(() => {
    return previewData?.createEvent || projectPreviewData?.createEvent || null;
  }, [previewData, projectPreviewData]);

  const formatDateTime = useCallback(
    (dateTimeZone?: { dateTime?: string; timeZone?: string }) => {
      if (!dateTimeZone?.dateTime) return '-';
      return moment(dateTimeZone.dateTime).format('dd, DD.MM.YYYY HH:mm');
    },
    []
  );

  const handleImport = async () => {
    if (!event) return;
    setLoading(true);

    try {
      const promises: Promise<any>[] = [];

      if (importPersonal) {
        promises.push(apiCreateCalendarEvent('me', event as CalendarEvent));
      }
      if (importProject && !isMe) {
        promises.push(
          apiCreateCalendarEvent(projectId, event as CalendarEvent)
        );
      }

      const results = await Promise.all(promises);
      const allSucceeded = results.every(
        (r) => r.result.status >= 200 && r.result.status < 300
      );

      if (allSucceeded) {
        notification.success({
          message: t('calendar:icsImport.success'),
        });
        onClose();
      } else {
        notification.error({
          message: t('calendar:icsImport.error'),
        });
      }
    } catch (error) {
      console.error('ICS import failed:', error);
      notification.error({
        message: t('calendar:icsImport.error'),
      });
    } finally {
      setLoading(false);
    }
  };
  //#endregion

  if (!event) return null;

  return (
    <Modal
      className={classes.root}
      title={t('calendar:icsImport.title')}
      visible={visible}
      onOk={handleImport}
      onClose={onClose}
      cancelText={t('calendar:icsImport.cancelText')}
      okText={t('calendar:icsImport.okText')}
      okButtonProps={{
        loading,
        disabled: !importPersonal && !importProject,
      }}
      destroyOnClose
    >
      <Flex.Column childrenGap={theme.old.spacing.unit(2)}>
        <Descriptions
          column={1}
          size="small"
          bordered
          className={classes.eventPreview}
        >
          <Descriptions.Item label={t('calendar:form.labels.subject')}>
            {event.subject || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('calendar:form.labels.start')}>
            {formatDateTime(event.start)}
          </Descriptions.Item>
          <Descriptions.Item label={t('calendar:form.labels.end')}>
            {formatDateTime(event.end)}
          </Descriptions.Item>
          {event.location?.displayName && (
            <Descriptions.Item label={t('calendar:form.labels.location')}>
              {event.location.displayName}
            </Descriptions.Item>
          )}
          {event.attendees?.length > 0 && (
            <Descriptions.Item label={t('calendar:form.labels.attendees')}>
              {event.attendees
                .map(
                  (a) =>
                    a.emailAddress?.name || a.emailAddress?.address || '-'
                )
                .join(', ')}
            </Descriptions.Item>
          )}
        </Descriptions>

        <Flex.Column childrenGap={theme.old.spacing.unit(1)}>
          <div className={classes.calendarSection}>
            <div className={classes.calendarRow}>
              <div className={classes.calendarLabel}>
                <Checkbox
                  checked={importPersonal}
                  onChange={(e) => setImportPersonal(e.target.checked)}
                />
                <FontAwesomeIcon icon={['fal', 'calendar']} />
                <span>{t('calendar:icsImport.personalCalendar')}</span>
              </div>
              {previewData?.alreadyExists && (
                <Tag color="orange" className={classes.existsTag}>
                  {t('calendar:icsImport.alreadyExists')}
                </Tag>
              )}
            </div>
          </div>

          {!isMe && (
            <div className={classes.calendarSection}>
              <div className={classes.calendarRow}>
                <div className={classes.calendarLabel}>
                  <Checkbox
                    checked={importProject}
                    onChange={(e) => setImportProject(e.target.checked)}
                  />
                  <FontAwesomeIcon icon={['fal', 'calendar-alt']} />
                  <span>{t('calendar:icsImport.projectCalendar')}</span>
                </div>
                {projectPreviewData?.alreadyExists && (
                  <Tag color="orange" className={classes.existsTag}>
                    {t('calendar:icsImport.alreadyExists')}
                  </Tag>
                )}
              </div>
            </div>
          )}
        </Flex.Column>
      </Flex.Column>
    </Modal>
  );
};

export default IcsImportModal;
